import { useCallback, useEffect, useRef, useState } from 'react';

import { OtpInput } from '@/auth/components/shared';
import { useSignupModalStyles as usePartnerSignupStyles } from '@/auth/components/shared/useSignupModalStyles';
import Button from '@/components/Button';
import { EmailDeliveryHelp } from '@/componentsv2/EmailDeliveryHelp';
import { ERROR_CODES, hasErrorCode } from '@/core/api';

import { STEP_COPY } from '../constants';
import type { useDomainMatch } from '../hooks/useDomainMatch';
import type { usePendingProjectInvite } from '../hooks/usePendingProjectInvite';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import { runPostVerifyLookups } from '../utils/postVerifyLookups';
import { endOnSignInRequired } from '../utils/signInRequired';
import { useStyles as useEmailOtpStyles } from './EmailOtpStep.styles';

// Step A.5 — email-OTP gate. Added 2026-05-08 by ENG-5201's phoneless
// pivot: replaces the partner-flow's SMS-OTP-as-auth step with an explicit
// email-OTP verification gate. Sits between AccountStep and the team-create
// or team-join branch.
//
// Flow:
//   1. On mount, dispatch sendPasswordlessOTP(transport: EMAIL).
//   2. User enters the 6-digit code. Submit calls verifyPasswordlessOTP
//      (transport: EMAIL); on success, store the token and fire
//      pendingProjectInvite + groupsByEmailDomain in parallel (ENG-5334). A
//      pending project invite takes priority over domain-match routing.
//   3. "Edit email" goes back to AccountStep (preserves form data).
//   4. "Resend code" re-fires sendPasswordlessOTP, subject to API rate limits.
//
// Email OTP is 6 digits (sha256-hashed, 10 attempts, rate-limited) per the
// API security trade-off in ENG-5201, matching the phone OTP length.

interface EmailOtpStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  domainMatch: ReturnType<typeof useDomainMatch>;
  pendingProjectInvite: ReturnType<typeof usePendingProjectInvite>;
  // Sends a fresh code to the user's signup email. Resolves on success.
  onSendEmailOtp: (email: string) => Promise<void>;
  // Verifies the code; returns the OTP token + expiry.
  onVerifyEmailOtp: (email: string, code: string) => Promise<{ token: string; expiresAt: number }>;
}

const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_OTP_LENGTH = 6;

export function EmailOtpStep({
  flow,
  domainMatch,
  pendingProjectInvite,
  onSendEmailOtp,
  onVerifyEmailOtp,
}: EmailOtpStepProps): JSX.Element {
  const s = usePartnerSignupStyles();
  const styles = useEmailOtpStyles();
  const [otpCode, setOtpCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const sentRef = useRef(false);
  const { state } = flow;
  const email = state.account.email ?? '';

  // Auto-send on first mount of this step. Guarded so React Strict Mode's
  // double-invoke doesn't burn through the rate limit.
  useEffect(() => {
    if (sentRef.current || !email) return;
    sentRef.current = true;
    void (async () => {
      try {
        await onSendEmailOtp(email);
        setResendCountdown(RESEND_COOLDOWN_SECONDS);
      } catch (err) {
        if (endOnSignInRequired(flow, err)) return;
        flow.setError(
          ERROR_CODES.AUTH_SEND_OTP_FAILED,
          err instanceof Error ? err.message : 'Send failed'
        );
      }
    })();
  }, [email, onSendEmailOtp, flow]);

  // Resend cooldown tick.
  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const t = setTimeout(() => setResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  const verify = useCallback(async () => {
    if (otpCode.length !== EMAIL_OTP_LENGTH || busy || !email) return;
    setBusy(true);
    flow.clearError();

    // OTP verification itself: only this call's failure means the code was
    // wrong/expired, so only this catch clears the code and reports it as
    // such.
    let otpResult: { token: string; expiresAt: number };
    try {
      otpResult = await onVerifyEmailOtp(email, otpCode);
    } catch (err) {
      // ENG-6106: a session refused for MFA setup ends on the sign-in panel.
      if (endOnSignInRequired(flow, err)) {
        setBusy(false);
        return;
      }
      // TLA+ contract obligation (AuthenticationFlow.tla): if the OTP token
      // has expired, route to the dedicated otpExpired action so the
      // reducer can bounce back to a fresh send instead of leaving the
      // user staring at a generic "Verify failed" alert.
      if (
        hasErrorCode(err, ERROR_CODES.AUTH_TOKEN_EXPIRED) ||
        hasErrorCode(err, ERROR_CODES.AUTH_INVALID_OTP_TOKEN)
      ) {
        flow.otpExpired();
      } else {
        flow.setError(
          ERROR_CODES.AUTH_OTP_INVALID,
          err instanceof Error ? err.message : 'Verify failed'
        );
      }
      setOtpCode('');
      setBusy(false);
      return;
    }

    const { token, expiresAt } = otpResult;
    flow.verifyEmailOtp(token, expiresAt);
    // A pending invite takes priority over domain-match team-create/team-join
    // branching; resolveEmailOtpVerification's reducer case owns that
    // decision, so both results are dispatched together.
    try {
      const lookups = await runPostVerifyLookups(
        { domainMatch, pendingProjectInvite },
        { email, otpToken: token, step: 'email-otp' }
      );
      if (lookups.status === 'refused') {
        // No generic error, no advancing.
        flow.setDomainMatchLoading(false);
        endOnSignInRequired(flow, lookups.error);
        return;
      }
      if (lookups.status === 'failed') {
        // Both legs failed -- nothing to route on. Reset loading so the
        // input/button re-enable, and keep the entered (valid) code
        // instead of clearing it, so the user can just retry rather than
        // blaming/re-sending the OTP.
        flow.setDomainMatchLoading(false);
        flow.setError(ERROR_CODES.UNKNOWN, 'Something went wrong. Please try again.');
      } else {
        flow.resolveEmailOtpVerification(lookups.pendingInvite, lookups.domainMatches);
      }
    } finally {
      setBusy(false);
    }
  }, [otpCode, busy, email, onVerifyEmailOtp, flow, domainMatch, pendingProjectInvite]);

  const resend = useCallback(async () => {
    if (resendCountdown > 0 || !email) return;
    try {
      flow.clearError();
      await onSendEmailOtp(email);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      setOtpCode('');
    } catch (err) {
      if (endOnSignInRequired(flow, err)) return;
      flow.setError(
        ERROR_CODES.AUTH_SEND_OTP_FAILED,
        err instanceof Error ? err.message : 'Resend failed'
      );
    }
  }, [resendCountdown, email, onSendEmailOtp, flow]);

  const editEmail = useCallback(() => flow.goBack(), [flow]);

  return (
    <>
      <div className={`${s.formContent} ${styles.content}`}>
        <header>
          <h2 id="self-service-smb-account-title" className={styles.title}>
            {STEP_COPY.emailOtp.title}
          </h2>
          <p className={styles.subtitle}>{STEP_COPY.emailOtp.subtitle(email)}</p>
        </header>
        {state.error && (
          <p role="alert" className={styles.errorText}>
            {state.error.message}
          </p>
        )}
        <OtpInput
          value={otpCode}
          onChange={setOtpCode}
          length={EMAIL_OTP_LENGTH}
          disabled={busy || state.domainMatchLoading}
          autoFocusFirstCell
          hasError={Boolean(state.error)}
        />
        <div className={styles.actionsRow}>
          <Button
            variant="text"
            size="normal"
            onClick={editEmail}
            disabled={busy}
            classes={{ root: s.backButton }}
          >
            {STEP_COPY.emailOtp.edit}
          </Button>
          <Button
            variant="text"
            size="normal"
            onClick={resend}
            disabled={busy || resendCountdown > 0}
            classes={{ root: s.backButton }}
          >
            {resendCountdown > 0
              ? `${STEP_COPY.emailOtp.resend} (${resendCountdown}s)`
              : STEP_COPY.emailOtp.resend}
          </Button>
        </div>
        <EmailDeliveryHelp className="self-end" />
      </div>
      <div className={s.footer}>
        <span />
        <Button
          color="teal"
          size="normal"
          onClick={verify}
          disabled={otpCode.length !== EMAIL_OTP_LENGTH || busy || state.domainMatchLoading}
          classes={{ root: s.continueButton }}
        >
          {state.domainMatchLoading ? 'Looking up your team…' : STEP_COPY.emailOtp.submit}
        </Button>
      </div>
    </>
  );
}
